import { Component, h, Fragment } from 'preact';
import { Localizer, Text } from 'preact-i18n';
import { FinText } from '../../../i18n';
import * as appStyles from '../../../assets/global.scss';
import {
  getKey,
  Key,
  LocationType,
  PreviousSearch,
  PREVIOUS_SEARCHES_KEY,
  TravelType,
  WidgetTab,
  SearchType,
} from '../../../common';
import Loader from '../../../common/Loader/Loader';
import { Modal, ModalBody, ModalHeader, ModalProps, ModalTitle } from '../../../modal';
import { Actions, BookingState, connectStore } from '../../../store';
import { withStyles, WithStyles } from '../../../style';
import { asCmsLang, debounce, isBlank, isClient, isEmpty, isPresent, stopPropagation } from '../../../utils';
import { AriaLiveResults } from './AriaLiveResults/AriaLiveResults';
import * as styles from './LocationModal.scss';
import LocationResult, { LocationItem } from './LocationResult/LocationResult';
import { GtmEvent, GtmService } from '../../../config';
import { PreviousSearches } from './PreviousSearches/PreviousSearches';
import { storage } from '../../../store/storage';
import { searchIsValid } from '../locationUtils';

export interface LocationModalBaseProps extends ModalProps {
  locationType: LocationType;
  locations: LocationItem[];
  onLocationSearch: (query: string) => void;
  onLocationSelect: (location: LocationItem) => void;
  listId: string;
  listTopBar?: h.JSX.Element;
  isLoading?: boolean;
  disableSearch?: boolean;
  locale: string;
  gtmService: GtmService;
}
interface StoreProps {
  currentTab: WidgetTab;
  travelType: TravelType;
}

type EffectiveProps = LocationModalBaseProps & StoreProps & Actions & WithStyles;

type LocationModalBaseState = {
  activeIndex: number;
  query: string;
  previousSearches: number;
};

enum ListState {
  LOADING,
  RESULTS,
  NO_RESULTS,
}

const trackLocationSearch = (service, event) => service.log(GtmEvent.UI_EVENT, event);
const debouncedSearchTracking = debounce(trackLocationSearch, 1000);

const mapStateToProps = ({ currentTab, selection }: BookingState): StoreProps => ({
  currentTab,
  travelType: selection.travelType,
});
// noinspection HtmlUnknownAttribute
@withStyles(styles, appStyles)
@connectStore({ mapStateToProps })
export default class LocationModalBase extends Component<LocationModalBaseProps, LocationModalBaseState> {
  input: HTMLInputElement;

  state = {
    activeIndex: 0,
    query: '',
    previousSearches: (storage.local.get<PreviousSearch[]>(PREVIOUS_SEARCHES_KEY) ?? []).filter(searchIsValid).length,
  };

  searchLocations = (): void => {
    const { currentTab } = this.props as EffectiveProps;
    // when searching always point to first item
    this.setState(() => ({ query: this.input.value, activeIndex: 0 }));
    this.props.onLocationSearch(this.input.value);

    if (this.input.value?.length >= 3) {
      debouncedSearchTracking(this.props.gtmService, {
        context: 'flight-search',
        triggerElement: 'BOOKING_WIDGET',
        id: `${this.props.listId}-modal-input`,
        state: this.input.value,
        language: asCmsLang(this.props.locale),
        pagePath: isClient() ? window.location.href : '',
        searchType: currentTab === WidgetTab.FLIGHT ? SearchType.BOOKING_FLOW : SearchType.AWARD,
      });
    }
  };

  selectLocation = (location: LocationItem): void => {
    const { currentTab } = this.props as EffectiveProps;
    this.context.modal.hideModal();
    this.props.onLocationSelect(location);
    this.props.gtmService.log(GtmEvent.UI_EVENT, {
      context: 'flight-search',
      triggerElement: 'BOOKING_WIDGET',
      id: `${this.props.listId}-modal-selection`,
      state: location.locationCode,
      language: asCmsLang(this.props.locale),
      pagePath: isClient() ? window.location.href : '',
      searchType: currentTab === WidgetTab.FLIGHT ? SearchType.BOOKING_FLOW : SearchType.AWARD,
    });
  };

  listItemId = (itemId: number): string => `${this.props.listId}-${itemId}`;

  scrollSelectionToView = (): number =>
    requestAnimationFrame(() => {
      const selected: HTMLElement = isPresent(this.base)
        ? (this.base as HTMLElement).querySelector(`#${this.listItemId(this.state.activeIndex)}`)
        : undefined;
      if (isPresent(selected)) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    });

  incrementActiveIndex(): void {
    const { locations } = this.props as EffectiveProps;
    const updatedIndex: number = this.state.activeIndex + 1;
    const newIndex: number =
      updatedIndex >= (isBlank(this.state.query) ? locations.length + this.state.previousSearches : locations.length)
        ? 0
        : updatedIndex;
    this.setState(() => ({ activeIndex: newIndex }));
    this.scrollSelectionToView();
  }

  decrementActiveIndex(): void {
    const { locations } = this.props as EffectiveProps;
    const updatedIndex: number = this.state.activeIndex - 1;
    const newIndex: number =
      updatedIndex < 0
        ? (isBlank(this.state.query) ? locations.length + this.state.previousSearches : locations.length) - 1
        : updatedIndex;
    this.setState(() => ({ activeIndex: newIndex }));
    this.scrollSelectionToView();
  }

  handleKeyDown = (e: KeyboardEvent): void => {
    const { isLoading, locations } = this.props as EffectiveProps;
    if (isEmpty(locations) || isLoading) {
      return;
    }

    const { activeIndex } = this.state;
    switch (getKey(e)) {
      case Key.ARROW_DOWN:
        stopPropagation(e);
        this.incrementActiveIndex();
        break;
      case Key.ARROW_UP:
        stopPropagation(e);
        this.decrementActiveIndex();
        break;
      case Key.ENTER:
        stopPropagation(e);
        this.selectLocation(locations[activeIndex]);
        break;
      default:
        break;
    }
  };

  onClose = () => {
    const { currentTab } = this.props as EffectiveProps;
    this.context.modal.hideModal();
    this.props.onLocationSearch('');
    this.props.gtmService?.log(GtmEvent.UI_EVENT, {
      context: 'flight-search',
      triggerElement: 'BOOKING_WIDGET',
      id: `${this.props.listId}-modal-close`,
      state: this.state.query,
      language: asCmsLang(this.props.locale),
      pagePath: isClient() ? window.location.href : '',
      searchType: currentTab === WidgetTab.FLIGHT ? SearchType.BOOKING_FLOW : SearchType.AWARD,
    });
  };

  previousSearchesClose = () => {
    this.onClose();
  };

  onDelete = (items: number) => {
    this.state.previousSearches = items;
    // use -1 to keep the current selection because we are removing item from the previous searches list
    this.setState({ ...this.state, activeIndex: this.state.activeIndex - 1 });
  };

  render(
    {
      locations,
      isLoading,
      locationType,
      listId,
      listTopBar,
      disableSearch,
      currentTab,
      travelType,
      tx,
    }: EffectiveProps,
    { query, activeIndex }: LocationModalBaseState
  ): h.JSX.Element {
    const results: ListState = isEmpty(locations) ? ListState.NO_RESULTS : ListState.RESULTS;
    const listState: ListState = isLoading ? ListState.LOADING : results;
    const emptySearch = isBlank(query);

    return (
      <Modal class={tx('modalRoot', 'bw-location-modal')} onClose={this.onClose}>
        <ModalHeader class={tx('modalHeader', 'border-bottom')}>
          <ModalTitle class={tx('ps-xsmall-b')} onClose={this.onClose}>
            <FinText id={`locationModal.${locationType}.title`} />
          </ModalTitle>
          <div
            class="ps-xxsmall-t"
            role="combobox"
            aria-owns={listId}
            aria-expanded={listState === ListState.RESULTS}
            aria-haspopup="listbox"
          >
            <Localizer>
              <input
                class={tx('focusInset', 'ps-xxsmall font-body-1 fill border-none')}
                type="text"
                name="locationSearch"
                placeholder={(<FinText id={`locationModal.${locationType}.inputPlaceholder`} />) as any}
                onCompositionEnd={this.searchLocations}
                onInput={this.searchLocations}
                autocomplete="off"
                autocapitalize="off"
                spellcheck={false}
                autocorrect="off"
                onKeyDown={this.handleKeyDown}
                id={`${locationType}-input`}
                aria-autocomplete="list"
                aria-controls={listId}
                aria-label={(<FinText id={`locationModal.${locationType}.inputPlaceholder`} />) as any}
                aria-activedescendant={`${listId}-${activeIndex}`}
                ref={(input) => (this.input = input)}
                autofocus
                value={query}
                disabled={disableSearch}
              />
            </Localizer>
          </div>
        </ModalHeader>
        <ModalBody class={tx('ps-xsmall-t')}>
          <div class="ps-large-x">{listTopBar}</div>

          {listState === ListState.LOADING && <Loader center wrapper={false} />}

          {emptySearch && travelType !== TravelType.MULTI_CITY && currentTab === WidgetTab.FLIGHT && (
            <PreviousSearches
              onModalClose={this.previousSearchesClose}
              onDelete={this.onDelete}
              data-test-prev-searches
              activeItem={activeIndex}
            />
          )}

          {!emptySearch && listState === ListState.NO_RESULTS && (
            <div class="ps-large-x font-body-1 break-word">
              <Text id={`locationModal.${locationType}.results`} plural={0} fields={{ count: '0', query }} />
            </div>
          )}

          {listState === ListState.RESULTS && (
            <Fragment>
              {emptySearch && (
                <div className={tx('grey-50-bg rounded-small ps-large-x ps-medium-y')}>
                  <h3 className="font-body-1 nordic-blue-900-text">
                    {currentTab !== WidgetTab.AM &&
                      (locationType === LocationType.DESTINATION ? (
                        <FinText id={'locationModal.trending'} />
                      ) : (
                        <FinText id={'locationModal.origin.nearestAiport'} />
                      ))}
                  </h3>
                </div>
              )}
              <Localizer>
                <ul
                  id={listId}
                  class={tx('unstyled')}
                  role="listbox"
                  aria-hidden={listState !== ListState.RESULTS}
                  aria-label={(<FinText id={`locationModal.${locationType}.listTitle`} />) as any}
                >
                  {locations.map((location: LocationItem, i: number) => (
                    <LocationResult
                      key={i}
                      rowClass={tx({ 'border-bottom': i !== locations.length - 1 })}
                      id={this.listItemId(i)}
                      active={(isBlank(query) ? i + this.state.previousSearches : i) === activeIndex}
                      locationItem={location}
                      onSelection={(locationItem) => this.selectLocation(locationItem)}
                    />
                  ))}
                </ul>
              </Localizer>
            </Fragment>
          )}
          <AriaLiveResults results={locations.length} locationType={locationType} isLoading={isLoading} query={query} />
        </ModalBody>
      </Modal>
    );
  }
}
